// src/context/CookieConsentContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  cookieConsentFlagsFromStored,
  loadCookieConsentFromStorage,
  persistCookieConsent,
} from '../utils/cookieConsentStorage';

export { COOKIE_CONSENT_STORAGE_KEY };

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const initial = typeof window !== 'undefined' ? loadCookieConsentFromStorage() : null;
  const [hasAcknowledged, setHasAcknowledged] = useState(initial?.acknowledged ?? false);
  const [consentFlags, setConsentFlags] = useState(() => cookieConsentFlagsFromStored(initial));
  const [settingsOpen, setSettingsOpen] = useState(false);

  const consent = useMemo(
    () => ({
      essential: true,
      analytics: consentFlags.analytics,
      advertising: consentFlags.advertising,
    }),
    [consentFlags.analytics, consentFlags.advertising],
  );

  const showBanner = !hasAcknowledged || settingsOpen;

  const applyConsent = useCallback((flags) => {
    persistCookieConsent(flags);
    setConsentFlags(cookieConsentFlagsFromStored({
      acknowledged: true,
      analytics: flags.analytics,
      advertising: flags.advertising,
    }));
    setHasAcknowledged(true);
    setSettingsOpen(false);
  }, []);

  const acceptAdvertising = useCallback(() => {
    applyConsent({ analytics: false, advertising: true });
  }, [applyConsent]);

  const rejectOptional = useCallback(() => {
    applyConsent({ analytics: false, advertising: false });
  }, [applyConsent]);

  const updateConsent = useCallback((partial) => {
    applyConsent({
      analytics: partial?.analytics ?? consentFlags.analytics,
      advertising: partial?.advertising ?? consentFlags.advertising,
    });
  }, [applyConsent, consentFlags.analytics, consentFlags.advertising]);

  const openCookieSettings = useCallback(() => {
    setSettingsOpen(true);
  }, []);

  const closeCookieSettings = useCallback(() => {
    setSettingsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      consent,
      hasAcknowledged,
      showBanner,
      acceptAdvertising,
      rejectOptional,
      updateConsent,
      openCookieSettings,
      closeCookieSettings,
    }),
    [
      consent,
      hasAcknowledged,
      showBanner,
      acceptAdvertising,
      rejectOptional,
      updateConsent,
      openCookieSettings,
      closeCookieSettings,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
}
